import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { App, Flex, Space, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"

import BulkEditModal from './components/BulkEditModal'
import BulkActionsToolbar from './components/BulkActionsToolbar'
import Button from "../../../Components/Button"
import EmptyComponent from "../../../Components/Empty"
import ExportDropdown from "../../../Components/Export"
import Filters from "../../../Components/Filters"
import GlobalFilterTestData from "../../../Components/GlobalFilterTestData/globalFilterTest"
import ManageColumn from "../../../Components/ManageColumns"
import Table from "../../../Components/Table"
import { getBaseColumns } from "../../../Components/Table/baseColumns"
import Icon from "../../../Icon"
import { modalConfig } from "../../../theme"
import { defaultFiltersByFolders, serializeData, useQuery } from "../../../Utils/utils"
import GlobalCreators from "../../GlobalFilters/reducer"
import RequestsCreators, { selectRequestResult } from "../reducer"
import { getActionColumn, getColumns, getDefaultColumn } from "./components/Table/columns"
import Creators, { selectAreFiltersDefault } from "./reducer"
import {useEnrollmentBulkActions} from './hooks/useEnrollmentBulkActions'


const pageSize = 50
const enrollmentsFilterOrder = ["Ethoca", "RDR", "CDRN"]
const previouslyConnectedEnrollmentsFilterOrder = ["Ethoca", "RDR", "CDRN", "I’m not sure"]

const { Text } = Typography

const defaultFiltersByFolder = defaultFiltersByFolders["enrollments"]

const RequestsEnrollmentsPage = ({
  isMobile,
  businessAccountId,
  visibleFiltersAdmin,
  defaultColumnsAdmin,
  columnsAdmin,
  fetching,
  result,
  filters,
  filterOptions,
  exportFetching,
  exportResult,
  saveResult,
  statistics,
  test_data,
  ...props
}) => {
  const navigate = useNavigate()
  const { search, pathname } = useLocation()
  let query = useQuery(search)
  let folder = query.get("folder")
  const { t } = useTranslation()

  const { notification, modal } = App.useApp()

  const [pageNumber, setPageNumber] = useState(1)
  const [ordering, setOrdering] = useState(null)

  const isFiltersDefault = useSelector((state) => selectAreFiltersDefault(state, folder))
  const requestResult = useSelector(selectRequestResult)

  const { count, results } = result

  const {
    bulkMode,
    bulkModalOpen,
    bulkRefetch,
    selectedRowKeys,
    selectedRows,
    changeStatusDisabled,
    isSameProvider,
    rowSelection,
    startBulkMode,
    cancelBulkMode,
    openBulkModal,
    closeBulkModal,
    handleBulkModalApplied,
  } = useEnrollmentBulkActions({
    results,
    pageNumber,
    filters,
    ordering,
    testData: test_data,
  })

  const myColumns = columnsAdmin
  const allFilters = defaultColumnsAdmin
  const visibleFilters = visibleFiltersAdmin

  const myColumnsLength = myColumns.filter(({ active }) => active).length
  const myColumnsHash = myColumns
    .filter(({ active }) => active)
    .map(({ field_id }) => field_id)
    .join("")
  const filterOptionsStatusHash = filterOptions?.["request_status"].join("")

  const statisticsFolder = statistics?.["enrollments_counter"]?.[folder || "all"] || 0

  const notificationConfig = {
    closeIcon: <Text>Dismiss</Text>,
    style: { minWidth: "max-content", width: 475 }
  }
  const handleClearFilters = () => {
    const newFilters = folder
      ? defaultFiltersByFolder[folder]
      : { request_status: "all", is_archived: null, is_unassigned: null }
    props.clearFilters(newFilters)
    props.clearGlobalFilters()
  }

  useEffect(() => {
    document.title = "Requests Enrollments - Chargeback"
    props.getFilterOptions()
  }, [])

  useEffect(() => {
    if (folder) {
      props.changeFilters(defaultFiltersByFolder[folder])
      if (pageNumber !== 1) setPageNumber(1)
    } else {
      props.changeFilters({ request_status: "all", is_archived: null, is_unassigned: null })
    }
  }, [folder])

  const filterOptionsModifiedByFolder = useMemo(
    () => ({
      ...filterOptions,
      request_status:
        !folder || defaultFiltersByFolder[folder]?.request_status === "all"
          ? filterOptions?.request_status
          : defaultFiltersByFolder[folder]?.request_status,
      enrollments: [
        ...enrollmentsFilterOrder.filter((value) =>
          (filterOptions?.enrollments || []).includes(value)
        ),
        ...(filterOptions?.enrollments || []).filter(
          (value) => !enrollmentsFilterOrder.includes(value)
        )
      ],
      previously_connected_enrollments: (() => {
        const previouslyEnrolledOptions = (
          filterOptions?.previously_enrolled ||
          filterOptions?.previously_connected_enrollments ||
          []
        ).filter((value) => value !== "All options")
        return [
          ...previouslyConnectedEnrollmentsFilterOrder.filter((value) =>
            previouslyEnrolledOptions.includes(value)
          ),
          ...previouslyEnrolledOptions.filter(
            (value) => !previouslyConnectedEnrollmentsFilterOrder.includes(value)
          )
        ]
      })()
    }),
    [filterOptions, folder]
  )

  const prevTestDataRef = useRef(test_data)

  useEffect(() => {
    if (saveResult || requestResult) return

    const testDataChanged = prevTestDataRef.current !== test_data
    if (testDataChanged) {
      prevTestDataRef.current = test_data
      if (pageNumber !== 1) {
        setPageNumber(1)
        return
      }
    }

    const serializeFilters = serializeData({
      ...filters,
      ...(folder &&
        (!filters["request_status"]?.length || filters["request_status"] === "all") && {
          request_status: defaultFiltersByFolder[folder]?.request_status
        })
    })
    const requestFilters = {
      ...serializeFilters,
      ...(serializeFilters.previously_connected_enrollments && {
        previously_enrolled: serializeFilters.previously_connected_enrollments
      })
    }
    delete requestFilters.previously_connected_enrollments

    const columns = myColumns.filter(({ active }) => active).map(({ field_id }) => field_id)
    const data = {
      page: pageNumber,
      page_size: pageSize,
      ordering,
      ...requestFilters,
      test_data,
      ...(columns.length && {
        columns: ["id", "request_status", "is_archived", "user_id", ...columns].join(",")
      })
    }
    props.getInfo({ ...data })
  }, [
    businessAccountId,
    ordering,
    pageNumber,
    filters,
    test_data,
    myColumnsLength,
    saveResult,
    requestResult,
    bulkRefetch
  ])

  useEffect(() => {
    if (exportResult) {
      notification.success({
        message: "Exporting",
        description:
          t(`${exportResult?.message}`) || t("Business accounts will be downloaded shortly."),
        ...notificationConfig
      })
      props.clearExportResult()
    }
  }, [exportResult])

  useEffect(() => {
    if (saveResult) {
      if (saveResult === "archived") {
        notification.success({
          message: "Archiving complete",
          ...notificationConfig,
          style: { minWidth: "max-content" }
        })
      }
      props.saveResultReset(null)
    }
  }, [saveResult])

  const tableSettings = useMemo(() => {
    const viewportHeight = window.innerHeight
    const fixedPixels = 72 + 24 + 32 + 24 + 43 + (16 + 32 + 16) + 16
    return {
      scroll: {
        x: "max-content",
        y: viewportHeight - fixedPixels,
        //x: count ? 'max-content' : (230 * myColumns.filter(({active}) => active).length),
        //y: 'calc(100vh - 72px - 24px - 32px - 24px - 43px - (16px + 32px + 16px) - 16px)',
        scrollToFirstRowOnChange: true
      },
      pagination: {
        total: count,
        current: pageNumber,
        pageSize: pageSize
      },
      locale: {
        emptyText: !statisticsFolder ? (
          <EmptyComponent type="requests_enrollments_admin" />
        ) : (
          <EmptyComponent type="search">
            <Button
              className="flex"
              icon={<Icon type={"menu_search"} />}
              onClick={handleClearFilters}
            >
              Clear search
            </Button>
          </EmptyComponent>
        )
      }
    }
  }, [myColumnsLength, count, pageNumber, statisticsFolder])

  const getTableColumns = useMemo(() => {
    const handleChangeStatus = ({ id, newStatus }) => {
      props.updateStatus({ id, request_status: newStatus })
    }

    let options = filterOptions?.["request_status"] || []

    let allColumns = {
      ...getBaseColumns({ onSelectRow: handleSelectRow, isAdmin: true }),
      ...getColumns({ options, handleChangeStatus, onSelectRow: handleSelectRow })
    }
    return [
      allColumns["status"],
      ...myColumns
        .filter(({ active }) => active)
        .map((data) => allColumns?.[data.field_id] || getDefaultColumn({ ...data })),
      getActionColumn({ onSelectRow: handleSelectRow })
    ]
  }, [myColumnsHash, filterOptionsStatusHash, search])

  const handleChangeTable = useCallback((pagination, filters, sorter) => {
    let ordering = null
    if (sorter) {
      if (Array.isArray(sorter)) {
        ordering = sorter
          ?.map(({ field, order }) => (order === "ascend" ? field : `-${field}`))
          .join(",")
      } else {
        if (sorter.order) {
          ordering = sorter.order === "ascend" ? sorter.field : `-${sorter.field}`
        }
      }
    }
    setPageNumber(pagination.current)
    setOrdering(ordering)
  }, [])

  const handleChangeFilter = useCallback((newData, type) => {
    if (newData?.user_email) {
      if (Array.isArray(newData.user_email) && newData.user_email.length > 0) {
        newData.user_email.forEach((email) => {
          if (email) props.addGlobalFilter("user_email", email.trim())
        })
      } else if (typeof newData.user_email === "string" && newData.user_email.trim()) {
        props.addGlobalFilter("user_email", newData.user_email.trim())
      } else {
        props.removeGlobalFilter("user_email")
      }
    }
    if (type === "changeVisible") {
      props.changeVisibleFilters("visibleFiltersAdmin", newData)
    } else {
      props.changeFilters(newData)
      setPageNumber(1)
    }
  }, [])

  const handleChangeColumns = useCallback((newColumns) => {
    props.changeColumns("columnsAdmin", newColumns)
  }, [])

  function handleSelectRow(data, type) {
    let business_account_id = data?.business_account?.id
    if (type === "business_account") {
      if (business_account_id) {
        navigate(`/business_settings/enrollment?${new URLSearchParams({ business_account_id })}`)
      }
    } else if (type === "dashboard") {
      navigate(`/admin/dashboard?business_account_id=${business_account_id}`)
    } else if (type === "delete") {
      modal.confirm({
        ...modalConfig("exclamation"),
        centered: !isMobile,

        title: "Confirm deleting",
        content: "Are you sure you want to delete this request? This action can NOT be undone.",

        cancelText: "Cancel",
        okText: "Delete",
        okButtonProps: {
          ...modalConfig("exclamation")?.okButtonProps,
          danger: true,
          ghost: true
        },
        onOk() {
          props.deleteRequest(data.id)
        },
        onCancel() {
          console.log("Cancel")
        }
      })
    } else if (type === "archive") {
      props.updateStatus({ id: data.id, is_archived: !data?.["is_archived"] })
    } else if (type === "add_comment") {
      props.selectRequestAddComment(data)
      const searchParams = new URLSearchParams(search)
      searchParams.set("add_comment", data?.id)
      navigate(`?${searchParams.toString()}`)
    } else if (type === "user_balance") {
      navigate(
        `/users/user_balance?user_id=${data?.user_id}&from=${encodeURIComponent(pathname + search)}`
      )
    }
  }

  const handleExport = useCallback(
    (type) => {
      const serializeFilters = serializeData(filters)
      props.getExport({
        file_name: "Requests enrollments report",
        ...(type !== "all" && serializeFilters),
        ...(type !== "all" ? { test_data } : {})
      })
    },
    [filters, test_data]
  )

  return (
    <div className="requests-page">
      {bulkMode ? (
        <BulkActionsToolbar
          t={t}
          selectedCount={selectedRowKeys.length}
          changeStatusDisabled={changeStatusDisabled}
          isSameProvider={isSameProvider}
          onOpenModal={openBulkModal}
          onCancel={cancelBulkMode}
        />
      ) : (
        <Flex gap={32} align="center" justify="space-between" style={{margin: '0 0 20px'}}>
          <Filters
            visibleFilters={visibleFilters}
            filterOptions={filterOptionsModifiedByFolder}
            withFolders={true}
            allFilters={allFilters}
            filters={filters}
            onChangeFilter={handleChangeFilter}
            filtersType={'requests'}
            newTagStyle='newTagStyle'
            showClearBtn={!isFiltersDefault}
            onClearFilters={handleClearFilters}
            addGlobalFilter={props.addGlobalFilter}
            removeGlobalFilter={props.removeGlobalFilter}
          />

          <Space style={{flexShrink: 0}}>
            <GlobalFilterTestData />
            <ExportDropdown loading={exportFetching}
                            onExport={handleExport}
                            isAdmin={true}
            />

            <ManageColumn
              myColumns={myColumns}
              onChangeColumns={handleChangeColumns}
            />

            <Button onClick={startBulkMode}>
              {t('Bulk action')}
            </Button>
          </Space>
        </Flex>
      )}

      <Table
        columns={getTableColumns}
        loading={fetching}
        dataSource={results}
        scroll={tableSettings.scroll}
        onChange={handleChangeTable}
        pagination={tableSettings.pagination}
        showSizeChanger={false}
        tableLayout="auto"
        //withOutPagination={true}
        locale={tableSettings.locale}
        rowSelection={rowSelection}
      />

      <BulkEditModal
        open={bulkModalOpen}
        onClose={closeBulkModal}
        requests={selectedRows}
        onApplied={handleBulkModalApplied}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  businessAccountId: state.auth.userInfo.current_business_account_id,
  isMobile: state.nav.isMobile,

  visibleFiltersAdmin: state.requestsEnrollments.visibleFiltersAdmin,

  defaultColumnsAdmin: state.requestsEnrollments.defaultColumnsAdmin,

  columns: state.requestsEnrollments.columns,
  columnsAdmin: state.requestsEnrollments.columnsAdmin,

  fetching: state.requestsEnrollments.fetching,
  result: state.requestsEnrollments.result,

  filters: state.requestsEnrollments.filters,
  filterOptions: state.requestsEnrollments.filterOptions,
  fetchingFilterOptions: state.requestsEnrollments.fetchingFilterOptions,

  exportFetching: state.requestsEnrollments.exportFetching,
  exportResult: state.requestsEnrollments.exportResult,

  saveResult: state.requestsEnrollments.saveResult,

  statistics: state.businessAccounts.statistics,
  test_data: state.globalFilters.testData
})

const mapDispatchToProps = (dispatch) => ({
  getFilterOptions: () => dispatch(Creators.getRequestsEnrollmentsFilterOptionsRequest()),

  getInfo: (params) => dispatch(Creators.getRequestsEnrollmentsRequest(params)),
  deleteRequest: (id) => dispatch(Creators.deleteRequestsEnrollmentRequest(id)),
  updateStatus: (params) => dispatch(Creators.updateRequestsEnrollmentRequest(params)),
  getExport: (businessId) => dispatch(Creators.getRequestsEnrollmentsExportRequest(businessId)),
  clearExportResult: () => dispatch(Creators.requestsEnrollmentsSetProp("exportResult", null)),
  changeColumns: (key, columns) => dispatch(Creators.requestsEnrollmentsSetProp(key, columns)),
  changeFilters: (filters) => dispatch(Creators.requestsEnrollmentsSetProp("filters", filters)),
  changeVisibleFilters: (visibleFiltersType, filters) =>
    dispatch(Creators.requestsEnrollmentsSetProp(visibleFiltersType, filters)),
  clearFilters: (data) => dispatch(Creators.clearRequestsEnrollmentsFilters(data)),
  saveResultReset: (value) => dispatch(Creators.requestsEnrollmentsSetProp("saveResult", value)),

  clearGlobalFilters: () => dispatch(GlobalCreators.clearGlobalFilters()),
  addGlobalFilter: (key, value) => dispatch(GlobalCreators.addGlobalFilter(key, value)),
  removeGlobalFilter: (key) => dispatch(GlobalCreators.removeGlobalFilter(key)),
  selectRequestAddComment: (request) =>
    dispatch(RequestsCreators.requestsSetProp("selectedRequest", request))
})

export default connect(mapStateToProps, mapDispatchToProps)(RequestsEnrollmentsPage)
